/**
 * LineChart: one polyline per series over a shared category axis, drawn from plain
 * data (ADR-026) on Recharts. Both axes are labeled (spec section 4), the legend
 * carries token swatches and toggles series, every point names itself through the
 * hover target and the tooltip, and the colours stay CSS variables so both themes
 * recolour without re-render.
 *
 * Author: Mahmoud Ragab Ahmed.
 */

import { useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { strings } from "../brand/strings";

import { ChartEmpty, CHART_MARGIN, Legend, seriesColor, surfaceClass } from "./shared";

export interface LineSeries {
  readonly label: string;
  readonly points: readonly number[];
}

interface LineDotProps {
  readonly cx?: number;
  readonly cy?: number;
  readonly index?: number;
  readonly payload?: Record<string, unknown>;
  /** Closed in from the series when the chart renders its dot element. */
  readonly seriesLabel?: string;
  readonly dataKey?: string;
  readonly color?: string;
}

/** One hover target per point: the visible dot plus the transparent reach ring. */
function LineDot(props: LineDotProps): React.JSX.Element | null {
  const { cx, cy, payload, seriesLabel, dataKey, color } = props;
  if (cx === undefined || cy === undefined || payload === undefined) {
    return null;
  }
  const categoryLabel = typeof payload.label === "string" ? payload.label : "";
  const rawValue = dataKey === undefined ? undefined : payload[dataKey];
  const value = typeof rawValue === "number" ? rawValue : Number(rawValue);
  const name =
    seriesLabel === undefined || Number.isNaN(value)
      ? undefined
      : strings.charts.point(seriesLabel, categoryLabel, value);
  return (
    <g>
      {/* The 8px+ hover target: a transparent disc behind the visible dot. */}
      <circle cx={cx} cy={cy} r={8} fill="transparent" />
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill={color ?? "var(--q-chart-1)"}
        stroke="var(--q-panel)"
        role="img"
        aria-label={name}
      >
        {name === undefined ? null : <title>{name}</title>}
      </circle>
    </g>
  );
}

export function LineChart({
  ariaLabel,
  labels,
  series,
}: {
  ariaLabel: string;
  /** The category axis labels, one per point position. */
  labels: readonly string[];
  series: readonly LineSeries[];
}): React.JSX.Element {
  const [hidden, setHidden] = useState<ReadonlySet<number>>(new Set());
  const nonEmpty = series.filter((one) => one.points.length > 0);
  const total = nonEmpty.reduce((sum, one) => sum + one.points.reduce((a, b) => a + b, 0), 0);
  if (labels.length === 0 || nonEmpty.length === 0 || total === 0) {
    return <ChartEmpty />;
  }
  const active = nonEmpty
    .map((one, index) => ({ one, index }))
    .filter(({ index }) => !hidden.has(index));
  const rows = labels.map((label, position) => {
    const row: Record<string, string | number> = { label };
    nonEmpty.forEach((one, index) => {
      row[`s${String(index)}`] = one.points[position] ?? 0;
    });
    return row;
  });
  // The value axis names itself after the only series when there is just one.
  const valueAxisLabel =
    active.length === 1
      ? (active[0]?.one.label ?? strings.charts.valueAxis)
      : strings.charts.valueAxis;

  return (
    <figure className="q-chart" data-testid="line-chart">
      <div className="q-chart__frame">
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={{ width: 640, height: 320 }}
        >
          <RechartsLineChart
            data={rows}
            margin={CHART_MARGIN}
            role="img"
            aria-label={ariaLabel}
            ref={surfaceClass}
          >
            <CartesianGrid stroke="var(--q-rule)" vertical={false} />
            <XAxis
              dataKey="label"
              interval={0}
              tickLine={false}
              axisLine={{ stroke: "var(--q-rule-strong)" }}
              label={{ value: strings.charts.categoryAxis, position: "insideBottom", offset: -2 }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number): string => String(Math.round(value))}
              label={{
                value: valueAxisLabel,
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle" },
              }}
            />
            <Tooltip
              isAnimationActive={false}
              content={({ active: hover, payload, label }) =>
                hover && payload.length > 0 ? (
                  <div className="q-chart__tooltip">
                    <p className="q-chart__tooltip-title">{String(label)}</p>
                    <ul className="q-chart__tooltip-list">
                      {payload.map((entry, index) => (
                        <li key={index} className="q-chart__tooltip-row">
                          <span
                            aria-hidden="true"
                            className="q-chart__tooltip-swatch"
                            style={{ background: entry.color }}
                          />
                          {entry.name}: {String(entry.value)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null
              }
            />
            {active.map(({ one, index }) => (
              <Line
                key={one.label}
                type="linear"
                dataKey={`s${String(index)}`}
                name={one.label}
                stroke={seriesColor(index)}
                strokeWidth={2}
                dot={
                  <LineDot
                    seriesLabel={one.label}
                    dataKey={`s${String(index)}`}
                    color={seriesColor(index)}
                  />
                }
                isAnimationActive={false}
              />
            ))}
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
      <Legend
        entries={nonEmpty.map((one, index) => ({
          label: one.label,
          index,
          active: !hidden.has(index),
        }))}
        onToggle={(index) => {
          setHidden((previous) => {
            const next = new Set(previous);
            if (next.has(index)) {
              next.delete(index);
            } else {
              next.add(index);
            }
            return next;
          });
        }}
      />
    </figure>
  );
}
